import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import ConfirmDialogShell from './ConfirmDialogShell';

const REASONS = [
  'previous_trip',
  'extended_boarding',
  'extended_unloading',
  'vehicle_issue',
  'operational_hold',
  'exceptional_incident',
  'other'
];

const delayMatchesBoundary = (delay, waypointId, boundary) => (
  Number(delay?.waypoint_id) === Number(waypointId)
  && delay.boundary_kind === boundary
);

const getMutationResult = (response) => {
  const envelope = response?.data;
  const data = envelope?.data;
  // A transport-level 2xx does not prove that persistence changed. The service
  // owns this decision and always returns a boolean so intentional idempotent
  // no-ops cannot be misreported as database updates.
  if (
    envelope?.success !== true
    || !data
    || typeof data !== 'object'
    || Array.isArray(data)
    || typeof data.changed !== 'boolean'
  ) {
    const error = new Error('Operational Delay mutation response is invalid');
    error.code = 'INVALID_OPERATIONAL_DELAY_RESPONSE';
    throw error;
  }
  return data;
};

const firstAvailableBoundary = (waypoints = []) => {
  for (const waypoint of waypoints) {
    if (waypoint.arrivalAllowed) return { waypointId: Number(waypoint.waypointId), boundary: 'arrival' };
    if (waypoint.departureAllowed) return { waypointId: Number(waypoint.waypointId), boundary: 'departure' };
  }
  return { waypointId: null, boundary: 'arrival' };
};

const OperationalDelayDialog = ({ isOpen, onClose, entityId, tripId, onChanged }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [waypointId, setWaypointId] = useState(null);
  const [boundary, setBoundary] = useState('arrival');
  const [minutes, setMinutes] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [action, setAction] = useState('set');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!entityId || !tripId) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/trips/${entityId}/${tripId}/operational-delays`);
      const next = response.data?.data || null;
      setState(next);
      const waypoints = next?.waypoints || [];
      const selected = waypoints.find((waypoint) => Number(waypoint.waypointId) === Number(waypointId));
      const selectedStoredBoundary = (next?.delays || []).find((delay) => (
        delayMatchesBoundary(delay, waypointId, boundary)
        && (delay.status === 'active' || delay.status === 'deleted')
        && delay.waypoint_in_current_order !== false
        && selected
      ));
      const selectedBoundaryStillValid = selected
        && (boundary === 'arrival' ? selected.arrivalAllowed : selected.departureAllowed);
      // Preserve consumed stored boundaries for inspection, but never keep an
      // absent/skipped waypoint as the form target. Its row remains visible as
      // a static history record while the editor falls back to a valid target.
      if (!selectedStoredBoundary && !selectedBoundaryStillValid) {
        const fallback = firstAvailableBoundary(waypoints);
        setWaypointId(fallback.waypointId);
        setBoundary(fallback.boundary);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('operationalDelay.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [boundary, entityId, tripId, t, waypointId]);

  useEffect(() => {
    if (isOpen) void load();
    // Loading is intentionally keyed to dialog identity/open state. Boundary
    // edits must not refetch and overwrite local form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId, tripId]);

  const waypoints = useMemo(() => state?.waypoints || [], [state?.waypoints]);
  const existingDelays = useMemo(() => {
    const waypointOrder = new Map(waypoints.map((waypoint, index) => (
      [Number(waypoint.waypointId), { waypoint, index }]
    )));

    // Editor state is already ordered by the authorized service. Keeping the
    // response order also preserves rows whose boundary is temporarily outside
    // the active route and therefore has no current select-option waypoint.
    return (state?.delays || [])
      .filter((delay) => delay?.status === 'active' || delay?.status === 'deleted')
      .map((delay) => ({ delay, ...waypointOrder.get(Number(delay.waypoint_id)) }));
  }, [state?.delays, waypoints]);
  const selectedWaypoint = useMemo(() => waypoints.find((waypoint) => (
    Number(waypoint.waypointId) === Number(waypointId)
  )) || null, [waypointId, waypoints]);
  const boundaryDelay = (state?.delays || []).find((delay) => (
    delayMatchesBoundary(delay, waypointId, boundary)
  ));
  const currentDelay = boundaryDelay?.status === 'active' ? boundaryDelay : null;

  useEffect(() => {
    if (!selectedWaypoint) return;
    if (currentDelay) {
      setAction('correct');
      setMinutes(String(currentDelay.delay_minutes || ''));
      setReasonCode(currentDelay.reason_code || '');
    } else {
      setAction('set');
      // A deleted row is current-state concurrency evidence, not an active
      // Delay. Its retained values make re-declaration convenient while its
      // exact updated_at token guards against overwriting a newer change.
      setMinutes(boundaryDelay?.status === 'deleted' ? String(boundaryDelay.delay_minutes || '') : '');
      setReasonCode(boundaryDelay?.status === 'deleted' ? boundaryDelay.reason_code || '' : '');
    }
  }, [boundary, boundaryDelay, currentDelay, selectedWaypoint, waypointId]);

  useEffect(() => {
    if (!selectedWaypoint) return;
    // Preserve the exact boundary of an existing active row for inspection.
    // Natural passage can make arrival unavailable while departure remains
    // selectable, but that must not silently retarget the selected Delay.
    if (boundaryDelay) return;
    if (boundary === 'arrival' && !selectedWaypoint.arrivalAllowed && selectedWaypoint.departureAllowed) {
      setBoundary('departure');
    } else if (boundary === 'departure' && !selectedWaypoint.departureAllowed && selectedWaypoint.arrivalAllowed) {
      setBoundary('arrival');
    }
  }, [boundary, boundaryDelay, selectedWaypoint]);

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    if (!selectedWaypoint || submitting) return;
    const delayMinutes = Number(minutes);
    if (action !== 'clear' && (!Number.isInteger(delayMinutes) || delayMinutes < 1 || delayMinutes > 1440)) {
      toast.error(t('operationalDelay.minutesInvalid'));
      return;
    }
    if ((action === 'correct' || action === 'clear') && !currentDelay) {
      await load();
      return;
    }
    const body = {
      expectedOrderVersion: Number(state.trip.waypoint_order_version || 0),
      ...(action === 'set' ? {
        waypointId: Number(selectedWaypoint.waypointId),
        boundary,
        ...(boundaryDelay?.status === 'deleted'
          ? { expectedUpdatedAt: boundaryDelay.updated_at }
          : {})
      } : {}),
      ...(action === 'clear' ? {} : { delayMinutes, reasonCode: reasonCode || null }),
      ...(currentDelay ? { expectedUpdatedAt: currentDelay.updated_at } : {})
    };

    setSubmitting(true);
    try {
      let response;
      let result;
      try {
        if (action === 'set') {
          response = await axios.post(`/api/trips/${entityId}/${tripId}/operational-delays`, body);
        } else if (action === 'correct') {
          response = await axios.put(`/api/trips/${entityId}/${tripId}/operational-delays/${currentDelay.id}`, body);
        } else {
          response = await axios.delete(`/api/trips/${entityId}/${tripId}/operational-delays/${currentDelay.id}`, { data: body });
        }
        result = getMutationResult(response);
      } catch (error) {
        toast.error(error.response?.data?.error || t('operationalDelay.saveFailed'));
        if (
          error.response?.status === 409
          || error.code === 'INVALID_OPERATIONAL_DELAY_RESPONSE'
        ) {
          await load();
        }
        return;
      }

      if (!result.changed) {
        toast.info(t('operationalDelay.noChanges'));
        await load();
        return;
      }

      toast.success(t('operationalDelay.saved'));
      try {
        await onChanged?.(result);
      } catch (_error) {
        // The mutation is already committed. A downstream refresh failure must
        // never relabel it as a failed save; the editor performs its own reload.
        toast.error(t('operationalDelay.loadFailed'));
      }
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedBoundaryAllowed = selectedWaypoint
    && (boundary === 'arrival' ? selectedWaypoint.arrivalAllowed : selectedWaypoint.departureAllowed);

  return (
    <ConfirmDialogShell
      isOpen={isOpen}
      onClose={close}
      onConfirm={submit}
      title={t('operationalDelay.title')}
      confirmLabel={action === 'clear' ? t('operationalDelay.withdraw') : t('operationalDelay.publish')}
      confirmVariant={action === 'clear' ? 'warning' : 'primary'}
      confirmIcon={Clock3}
      isLoading={submitting}
      isDisabled={loading || !selectedBoundaryAllowed}
      showIcon
      iconVariant="warning"
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">{t('operationalDelay.explanation')}</p>
      {state && (
        <section aria-labelledby="operational-delay-existing-title">
          <h3 id="operational-delay-existing-title" className="mb-2 text-sm font-semibold">
            {t('operationalDelay.existingTitle')}
          </h3>
          {existingDelays.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('operationalDelay.noExisting')}
            </p>
          ) : (
            <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
              {existingDelays.map(({ delay, waypoint }) => {
                const isCurrentOrder = delay.waypoint_in_current_order !== false && Boolean(waypoint);
                const isSelected = delayMatchesBoundary(delay, waypointId, boundary);
                const isEditable = isCurrentOrder && (delay.boundary_kind === 'arrival'
                  ? waypoint.arrivalAllowed
                  : waypoint.departureAllowed);
                const unavailableId = `operational-delay-unavailable-${delay.id}`;
                const waypointName = delay.waypoint_name || waypoint?.name || t('operationalDelay.waypointUnavailable');
                const boundaryLabel = t(`operationalDelay.${delay.boundary_kind}Boundary`);
                const isTemporarilyNotApplied = delay.status === 'active' && (
                  !isCurrentOrder || delay.waypoint_is_skipped === true
                );
                const statusLabel = t(`operationalDelay.${
                  isTemporarilyNotApplied
                    ? 'statusPaused'
                    : (delay.status === 'active' ? 'statusActive' : 'statusWithdrawn')
                }`);
                const accessibleName = `${waypointName}, ${boundaryLabel}, +${delay.delay_minutes} ${t('min')}, ${statusLabel}`;
                const rowContent = (
                  <>
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{waypointName}</span>
                        <span className="block text-xs text-gray-600 dark:text-gray-300">
                          {boundaryLabel}
                        </span>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                          +{delay.delay_minutes} {t('min')}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          delay.status === 'active' && !isTemporarilyNotApplied
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {statusLabel}
                        </span>
                      </span>
                    </span>
                    {delay.reason_code && (
                      <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300">
                        {t('operationalDelay.reason')}: {t(`operationalDelay.reasons.${delay.reason_code}`)}
                      </span>
                    )}
                    {delay.waypoint_is_skipped === true && (
                      <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300">
                        {t('operationalDelay.stopSkipped')}
                      </span>
                    )}
                    {isCurrentOrder && !isEditable && (
                      <span id={unavailableId} className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                        {t('operationalDelay.notEditable')}
                      </span>
                    )}
                    {isTemporarilyNotApplied && (
                      <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                        {t('operationalDelay.waypointTemporarilyNotApplied')}
                      </span>
                    )}
                    {!isCurrentOrder && delay.status !== 'active' && (
                      <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                        {t('operationalDelay.waypointNotInTrip')}
                      </span>
                    )}
                  </>
                );
                if (!isCurrentOrder) {
                  return (
                    <div
                      key={delay.id}
                      role="group"
                      aria-label={accessibleName}
                      data-testid={`operational-delay-row-${delay.id}`}
                      className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-left opacity-80 dark:border-gray-600 dark:bg-gray-800"
                    >
                      {rowContent}
                    </div>
                  );
                }
                return (
                  <button
                    key={delay.id}
                    type="button"
                    aria-label={accessibleName}
                    data-testid={`operational-delay-row-${delay.id}`}
                    onClick={() => {
                      setWaypointId(Number(delay.waypoint_id));
                      setBoundary(delay.boundary_kind);
                    }}
                    disabled={loading || submitting}
                    aria-pressed={isSelected}
                    aria-describedby={!isEditable ? unavailableId : undefined}
                    className={`w-full rounded-md border px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                        : delay.status === 'deleted'
                          ? 'border-gray-300 bg-gray-100 opacity-80 hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700'
                          : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-gray-800'
                    }`}
                  >
                    {rowContent}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}
      <label className="block text-sm font-medium">
        {t('operationalDelay.waypoint')}
        <select className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-gray-900" value={waypointId || ''} onChange={(event) => setWaypointId(Number(event.target.value))} disabled={submitting}>
          {waypoints.map((waypoint) => (
            <option key={waypoint.waypointId} value={waypoint.waypointId} disabled={!waypoint.arrivalAllowed && !waypoint.departureAllowed}>
              {waypoint.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm font-medium">
        {t('operationalDelay.boundary')}
        <select className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-gray-900" value={boundary} onChange={(event) => setBoundary(event.target.value)} disabled={submitting}>
          <option value="arrival" disabled={!selectedWaypoint?.arrivalAllowed}>{t('operationalDelay.arrivalBoundary')}</option>
          <option value="departure" disabled={!selectedWaypoint?.departureAllowed}>{t('operationalDelay.departureBoundary')}</option>
        </select>
      </label>
      {currentDelay && (
        <label className="block text-sm font-medium">
          {t('operationalDelay.action')}
          <select className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-gray-900" value={action} onChange={(event) => setAction(event.target.value)} disabled={submitting}>
            <option value="correct">{t('operationalDelay.correct')}</option>
            <option value="clear">{t('operationalDelay.withdraw')}</option>
          </select>
        </label>
      )}
      {action !== 'clear' && (
        <>
          <label className="block text-sm font-medium">
            {t('operationalDelay.minutes')}
            <input className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-gray-900" type="number" min="1" max="1440" step="1" value={minutes} onChange={(event) => setMinutes(event.target.value)} disabled={submitting} />
          </label>
          <label className="block text-sm font-medium">
            {t('operationalDelay.reason')}
            <select className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-gray-900" value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} disabled={submitting}>
              <option value="">{t('operationalDelay.noReason')}</option>
              {REASONS.map((reason) => <option key={reason} value={reason}>{t(`operationalDelay.reasons.${reason}`)}</option>)}
            </select>
          </label>
        </>
      )}
    </ConfirmDialogShell>
  );
};

export default OperationalDelayDialog;
